
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, MessageCircle, User, Calendar, BookOpen, UserPlus, Mail, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { pb } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';
import { isAdmin } from '@/utils/roleValidation';
import { logger } from '@/utils/secureLogger';
import { handleError } from '@/utils/errorHandler';

interface AdminNotification {
  id: string;
  type: 'admin_notification';
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  data?: {
    originalType?: string;
    [key: string]: any;
  };
}

const AdminNotificationBadge = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const subscriptionRef = useRef<(() => void) | null>(null);
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    logger.debug('AdminNotificationBadge mount', { 
      hasUser: !!user, 
      userRole: user?.role, 
      isAdmin: isAdmin(user) 
    });
    
    // Clean up previous subscription if user changed
    if (currentUserIdRef.current !== user?.id) {
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current();
        } catch (error) {
          logger.warn('Error cleaning up previous admin subscription', { error });
        }
        subscriptionRef.current = null;
      }
      currentUserIdRef.current = user?.id || null;
    }

    if ((isAdmin(user) || user?.role === 'assistant') && pb.authStore.isValid) {
      logger.debug('Admin or assistant user detected, setting up notifications');
      fetchNotifications();
      setupSubscription();
    } else {
      logger.debug('User is not admin/assistant or not authenticated');
      setNotifications([]);
      setUnreadCount(0);
    }

    return () => {
      logger.debug('Cleaning up admin notification subscription');
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current();
        } catch (error) {
          logger.warn('Error cleaning up admin subscription on unmount', { error });
        }
        subscriptionRef.current = null;
      }
    };
  }, [user?.id, user?.role, pb.authStore.isValid]);

  const setupSubscription = async () => {
    if (!user?.id || (user?.role !== 'admin' && user?.role !== 'assistant') || !pb.authStore.isValid) return;

    // Clean up existing subscription first to prevent duplicates
    if (subscriptionRef.current) {
      try {
        subscriptionRef.current();
      } catch (error) {
        logger.warn('Error cleaning up previous admin subscription in setup', { error });
      }
      subscriptionRef.current = null;
    }

    try {
      logger.debug('Setting up admin notification subscription');
      
      const unsubscribe = await pb.collection('notifications').subscribe('*', (e) => {
        logger.debug('Admin notification event received', { 
          action: e.action, 
          isForCurrentUser: e.record.userId === user.id,
          isAdminNotification: e.record.type === 'admin_notification'
        });
        
        if (e.record.userId === user.id && e.record.type === 'admin_notification') {
          if (e.action === 'create') {
            const notification = e.record as unknown as AdminNotification;
            // Check if notification already exists to prevent duplicates
            setNotifications(prev => {
              const exists = prev.some(n => n.id === notification.id);
              if (exists) {
                logger.debug('Admin notification already exists, skipping duplicate');
                return prev;
              }
              return [notification, ...prev];
            });
            if (!notification.isRead) {
              setUnreadCount(prev => prev + 1);
            }
          } else if (e.action === 'update') {
            setNotifications(prev => 
              prev.map(n => n.id === e.record.id ? e.record as unknown as AdminNotification : n)
            );
          }
        }
      });

      subscriptionRef.current = unsubscribe;
      logger.debug('Admin subscription established');
    } catch (error) {
      // Silently fail - realtime is not critical for the app to function
      logger.warn('Failed to setup admin notification subscription, will rely on polling', { error });
    }
  };

  const fetchNotifications = async () => {
    if (!user?.id || (user?.role !== 'admin' && user?.role !== 'assistant') || !pb.authStore.isValid) {
      logger.debug('Cannot fetch notifications - user not admin/assistant or not authenticated');
      return;
    }
    
    try {
      logger.debug('Fetching admin notifications');
      
      const records = await pb.collection('notifications').getList(1, 100, {
        filter: `userId = "${user.id}" && type = "admin_notification"`,
        sort: '-createdAt'
      });
      
      setNotifications(records.items as unknown as AdminNotification[]);
      // Calculate unread count from fetched notifications
      const unreadNotifications = records.items.filter((n: any) => !n.isRead);
      setUnreadCount(unreadNotifications.length);
      logger.debug('Unread count updated:', unreadNotifications.length);
    } catch (error) {
      handleError(error, {
        component: 'AdminNotificationBadge',
        action: 'fetchNotifications',
        userId: user?.id
      });
    }
  };

  // Update unread count based on current notifications state
  useEffect(() => {
    const unreadNotifications = notifications.filter(n => !n.isRead);
    setUnreadCount(unreadNotifications.length);
    logger.debug('Unread count recalculated from state:', unreadNotifications.length);
  }, [notifications]);

  const markAsRead = async (notificationId: string) => {
    if (!pb.authStore.isValid) return;
    
    try {
      // Optimistically update UI
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Update in database
      await pb.collection('notifications').update(notificationId, {
        isRead: true
      });
    } catch (error) {
      // Revert on error
      fetchNotifications();
      handleError(error, {
        component: 'AdminNotificationBadge',
        action: 'markAsRead'
      });
    }
  };

  const markAllAsRead = async () => {
    if (!pb.authStore.isValid) return;
    
    try {
      const unreadNotifications = notifications.filter(n => !n.isRead);
      for (const notification of unreadNotifications) {
        await pb.collection('notifications').update(notification.id, {
          isRead: true
        });
      }
      setUnreadCount(0);
    } catch (error) {
    }
  };

  const handleNotificationClick = async (notification: AdminNotification) => {
    // Mark as read
    await markAsRead(notification.id);
    
    // Navigate based on notification type and user role
    const originalType = notification.data?.originalType;
    const userRole = user?.role;
    
    console.log('🔔 Admin notification clicked:', { 
      title: notification.title, 
      originalType, 
      userRole,
      fullData: notification.data,
      isAdmin: isAdmin(user)
    });
    
    // Special handling for live chat notifications based on title as fallback
    if (notification.title?.toLowerCase().includes('live chat')) {
      console.log('🎯 Live chat notification detected by title, routing to live-chat-support');
      if (isAdmin(user)) {
        navigate('/admin/live-chat-support');
      } else if (userRole === 'assistant') {
        navigate('/live-chat-support');
      }
      setIsOpen(false);
      return;
    }
    
    switch (originalType) {
      case 'profile_update':
        // Admin only
        if (isAdmin(user)) {
          navigate('/admin/users');
        }
        break;
        
      case 'new_tutor':
        // Admin only
        if (isAdmin(user)) {
          navigate('/admin/tutors');
        }
        break;
        
      case 'new_student':
        // Admin only
        if (isAdmin(user)) {
          navigate('/admin/students');
        }
        break;
        
      case 'new_booking':
        // Admin: admin dashboard, Student/Tutor: schedule
        if (isAdmin(user)) {
          navigate('/admin/bookings');
        } else if (userRole === 'student' || userRole === 'tutor') {
          navigate('/schedule');
        }
        break;
        
      case 'subject_request':
        // Admin only
        if (isAdmin(user)) {
          navigate('/admin/subject-management');
        }
        break;
        
      case 'new_lesson':
        // Admin: admin lessons, Tutor: tutor lessons, Student: lessons
        if (isAdmin(user)) {
          navigate('/admin/lessons');
        } else if (userRole === 'tutor') {
          navigate('/tutor-lessons');
        } else if (userRole === 'student') {
          navigate('/lessons');
        }
        break;
        
      case 'contact_form':
        // Admin and assistant
        if (isAdmin(user)) {
          navigate('/admin/support-contacts');
        } else if (userRole === 'assistant') {
          navigate('/live-chat-support');
        }
        break;
        
      case 'live_chat':
      case 'new_live_chat':
      case 'live_chat_message':
      case 'live_chat_started':
      case 'new_live_chat_started':
        // Admin and assistant to live chat
        if (isAdmin(user)) {
          navigate('/admin/live-chat-support');
        } else if (userRole === 'assistant') {
          navigate('/live-chat-support');
        }
        break;
        
      case 'new_message':
        // All roles: messages page
        if (isAdmin(user)) {
          navigate('/support-messages');
        } else {
          navigate('/messages');
        }
        break;
        
      case 'profile_suspension_request':
        // Admin only - navigate to profile suspensions page
        if (isAdmin(user)) {
          navigate('/admin/user-management');
        }
        break;
        
      default:
        // Default navigation based on role
        if (isAdmin(user)) {
          navigate('/admin');
        } else if (userRole === 'assistant') {
          navigate('/live-chat-support');
        } else if (userRole === 'tutor') {
          navigate('/tutor-profile');
        } else if (userRole === 'student') {
          navigate('/dashboard');
        }
        break;
    }
    
    // Close the popover
    setIsOpen(false);
  };

  const getNotificationIcon = (originalType?: string) => {
    switch (originalType) {
      case 'new_message':
        return <MessageCircle className="h-4 w-4 text-blue-600" />;
      case 'new_tutor':
        return <UserPlus className="h-4 w-4 text-green-600" />;
      case 'new_student':
        return <User className="h-4 w-4 text-purple-600" />;
      case 'new_booking':
        return <Calendar className="h-4 w-4 text-orange-600" />;
      case 'subject_request':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'new_lesson':
        return <BookOpen className="h-4 w-4 text-indigo-600" />;
      case 'contact_form':
        return <Mail className="h-4 w-4 text-pink-600" />;
      case 'profile_update':
        return <User className="h-4 w-4 text-blue-600" />;
      case 'profile_suspension_request':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Bell className="h-4 w-4 text-gray-600" />;
    }
  };

  const getNotificationColor = (originalType?: string) => {
    switch (originalType) {
      case 'new_message':
        return 'border-blue-200 bg-blue-50';
      case 'new_tutor':
        return 'border-green-200 bg-green-50';
      case 'new_student':
        return 'border-purple-200 bg-purple-50';
      case 'new_booking':
        return 'border-orange-200 bg-orange-50';
      case 'subject_request':
        return 'border-yellow-200 bg-yellow-50';
      case 'new_lesson':
        return 'border-indigo-200 bg-indigo-50';
      case 'contact_form':
        return 'border-pink-200 bg-pink-50';
      case 'profile_update':
        return 'border-blue-200 bg-blue-50';
      case 'profile_suspension_request':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (user?.role !== 'admin' && user?.role !== 'assistant') {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative h-7 w-7 p-0">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0 z-[99999]" align="end" side="bottom">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Admin Notifications</h3>
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={markAllAsRead}
                className="text-xs"
              >
                Mark all read
              </Button>
            )}
          </div>
        </div>
        
        <ScrollArea className="h-80">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            <div className="p-2">
              {notifications.map((notification) => (
                <Card 
                  key={notification.id} 
                  className={`mb-2 transition-colors cursor-pointer hover:shadow-md ${
                    !notification.isRead ? getNotificationColor(notification.data?.originalType) : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-3 flex-1">
                        {getNotificationIcon(notification.data?.originalType)}
                        <div className="flex-1">
                          <h4 className="font-medium text-sm">{notification.title}</h4>
                          <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
                          <p className="text-xs text-gray-400 mt-2">{formatDate(notification.createdAt)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!notification.isRead ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                            className="h-8 w-8 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        ) : (
                          <div className="h-2 w-2 bg-gray-300 rounded-full"></div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default AdminNotificationBadge;
